import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { humanize } from 'underscore.string';
import { uniq } from 'lodash';
import { CSV_FILE_TYPES } from 'config/accepted-file-types';
import { FileUploader } from 'components/v2/FileUploader';
import Badge from 'components/v2/Badge';
import Checkbox from 'components/v2/forms/Checkbox';
import CargomaticInput from 'components/v2/input';
import { milestoneEdiEvents } from 'components/modules/shipments/mappings';
import Alert from 'components/ui/Alert';
import LoadingCircle from 'components/ui/LoadingCircle';
import { convertCommaSeparatedStringToArray } from 'helpers/strings';
import { showToast } from 'stores/ToastStore';
import {
  runPrecheck,
  resendEDIShipmentMilestones,
  resendEDIShipmentMilestonesBulk,
} from 'utils/ShipmentWebAPIUtils';
import { sluggify } from 'utils/localization/utils';
import { title } from 'process';
import CustomerStatus from './CustomerStatus';
import ReferenceNumberTable from './ReferenceNumberTable';

export default function Milestones() {
  const { t } = useTranslation();
  const [checkedEventsMap, setCheckedEventsMap] = useState({});
  const [results, setResults] = useState([]);
  const [runningPrecheck, setRunningPrecheck] = useState(false);
  const [resendingEvents, setResendingEvents] = useState(false);
  const [shipperStatusList, setShipperStatusList] = useState([]);
  const [text, setText] = useState('');
  const [referenceNumbers, setReferenceNumbers] = useState([]);
  const [errors, setErrors] = useState({});
  const [ranPrecheck, setRanPrecheck] = useState(false);
  const [resentEDIError, setResendEDIError] = useState(false);
  const [resendEDISuccess, setResendEDISuccess] = useState(false);
  const [resendEDISuccessMessage, setResendEDISuccessMessage] = useState('');
  const [showRunPrecheckBanner, setShowRunPrecheckBanner] = useState(true);

  const [uploadPending, setUploadPending] = useState(false);
  const [files, setFiles] = useState([]);
  const fileUploaderRef = useRef(null);

  useEffect(() => {
    document.title = `${t('RESEND_EDI')} - ${t('MILESTONES')}`;
  }, []);

  function addFiles(files) {
    // in case there's anything in these fields beforehand
    setText('');
    setReferenceNumbers([]);

    if (files) {
      setFiles(files);
    }
  }

  const clearFiles = () => {
    fileUploaderRef.current.clearFiles();
    setFiles([]);
  };

  function isFormEmpty() {
    return (
      Object.keys(checkedEventsMap).length === 0 ||
      (referenceNumbers.length === 0 && files.length === 0)
    );
  }

  function isRunPrecheckDisabled() {
    return isFormEmpty() || runningPrecheck;
  }

  function isResendDisabled() {
    return isFormEmpty() || resendingEvents;
  }

  function handleHideEDIResendAlert() {
    setResendEDISuccess(false);
    setResendEDIError(false);
  }

  function handleHideRunPrecheckAlert() {
    setShowRunPrecheckBanner(false);
  }

  function handleReset() {
    clearFiles();
    setText('');
    setReferenceNumbers([]);
    setRanPrecheck(false);
    setCheckedEventsMap({});
    setShipperStatusList([]);
    setResults([]);
    setResendEDISuccess(false);
    setResendEDISuccessMessage('');
    setResendEDIError(false);
    setShowRunPrecheckBanner(true);
  }

  function handleToggleEvent(event) {
    return function (checked) {
      setRanPrecheck(false);
      setCheckedEventsMap((map) => {
        const updated = { ...map };

        if (checked) {
          updated[event.slug] = true;
        } else {
          delete updated[event.slug];
        }
        return updated;
      });
    };
  }

  function handleTextChange(updatedText) {
    setText(updatedText);
  }

  async function handleParseReferenceNumbers() {
    const newReferenceNumbers = convertCommaSeparatedStringToArray(text);
    setReferenceNumbers((current) =>
      uniq([...current, ...newReferenceNumbers]),
    );
    setText('');
    setRanPrecheck(false);
  }

  function processPayload() {
    const events = milestoneEdiEvents
      .filter((ediEvent) => checkedEventsMap[ediEvent.slug])
      .map((ediEvent) => ediEvent.slug);

    const validationErrors = {};
    if (events.length === 0) {
      validationErrors.events = t('SELECT_ONE_EVENT');
    }
    if (referenceNumbers.length === 0 && files.length === 0) {
      validationErrors.referenceNumbers = t('PROVIDE_ATLEAST_ONE_REF_NUM');
    }
    setErrors(validationErrors);

    return { events, validationErrors };
  }

  async function handleRunPrecheck() {
    const { events, validationErrors } = processPayload();

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    try {
      setRanPrecheck(false);
      setRunningPrecheck(true);
      setResults([]);
      const result = await runPrecheck({
        userType: 'shipper',
        referenceNumbers,
        events,
      });
      setShipperStatusList(result.entity.reportData);
      setRanPrecheck(true);
      setShowRunPrecheckBanner(true);
    } catch (e) {
      console.error(e);
    } finally {
      setRunningPrecheck(false);
      setText('');
    }
  }

  async function handleUpload() {
    const { events, validationErrors } = processPayload();

    if (files.length !== 1) {
      validationErrors.invalid_file_amount = t('ATTACH_VALID_CSV_FILE');
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setUploadPending(true);

    try {
      await resendEDIShipmentMilestonesBulk({
        file: files[0],
        events,
      });
      showToast(t('CSV_UPLOAD_SUCCESSFUL'), t('DATA_UPLOADED_WAIT_FOR_EMAIL'));
      setResendEDISuccess(true);
      setResendEDISuccessMessage(t('DATA_UPLOADED_WAIT_FOR_EMAIL'));
    } catch (e) {
      const errorMsg = e?.response?.body?.message || 'SOMETHING_WENT_WRONG';
      showToast(t('CSV_UPLOAD_FAILED'), t(sluggify(errorMsg), 'error'));
      setResendEDIError(t(sluggify(errorMsg)));
    } finally {
      setUploadPending(false);
    }
  }

  async function handleResendEvents() {
    const { events, validationErrors } = processPayload();

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    try {
      setResendingEvents(true);
      setResendEDIError(false);
      setResendEDISuccess(false);

      const result = await resendEDIShipmentMilestones({
        reference_numbers: referenceNumbers,
        events,
      });
      setResults(result.entity.reportData);
      setShipperStatusList([]);
      setResendEDISuccess(true);
    } catch (e) {
      console.error(e);
      setResendEDIError(true);
    } finally {
      setText('');
      setResendingEvents(false);
    }
  }

  function handleRemove(referenceNumber) {
    setReferenceNumbers(
      referenceNumbers.filter((refNum) => refNum !== referenceNumber),
    );
    setRanPrecheck(false);
  }

  function renderEvents() {
    return (
      <div>
        <label className="mb-3 form-label">{t('MILESTONES_LABEL')}</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          {milestoneEdiEvents.map((ediEvent) => {
            return (
              <div key={ediEvent.slug}>
                <Checkbox
                  id={ediEvent.slug}
                  name={ediEvent.slug}
                  checked={checkedEventsMap[ediEvent.slug]}
                  onChange={handleToggleEvent(ediEvent)}
                >
                  {t(ediEvent.localizationSlug)}
                </Checkbox>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderShipperRows() {
    if (shipperStatusList.length === 0) {
      return null;
    }

    return (
      <div className="">
        {showRunPrecheckBanner && (
          <Alert
            type="info"
            className="mb-4"
            onRemove={handleHideRunPrecheckAlert}
          >
            {t('PRECHECK_MESSAGE')}
          </Alert>
        )}
        <CustomerStatus userType="shipper" rows={shipperStatusList} />
      </div>
    );
  }

  function renderAlerts() {
    let type;
    let message;
    if (resendEDISuccess) {
      type = 'success';
      message = resendEDISuccessMessage || t('RESEND_EDI_SUCCESS');
    } else if (resentEDIError) {
      type = 'error';
      message = t('RESEND_EDI_ERROR');
    }
    return (
      <div className="">
        {message && (
          <Alert
            className="mb-4"
            type={type}
            onRemove={handleHideEDIResendAlert}
          >
            {message}
          </Alert>
        )}
      </div>
    );
  }

  function renderResults() {
    if (results.length === 0) {
      return null;
    }

    return (
      <div className="">
        {renderAlerts()}
        <CustomerStatus userType="shipper" rows={results} />
      </div>
    );
  }

  function renderReferenceNumbers() {
    if (referenceNumbers.length === 0) {
      return null;
    }

    const label = t('REFERENCE_NUMBER');
    return (
      <div className="mt-6">
        <ReferenceNumberTable
          referenceNumbers={referenceNumbers}
          onRemove={handleRemove}
          label={label}
        />
      </div>
    );
  }

  function renderErrors() {
    if (Object.keys(errors).length === 0) {
      return null;
    }

    return (
      <Alert type="error" className="my-4">
        <ul>
          {Object.values(errors).map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      </Alert>
    );
  }

  return (
    <div className="">
      <div className="">
        <div className="bg-white shadow rounded-md">
          <div className="px-4 py-5 sm:p-6">
            {renderEvents()}
            <hr />
            <label className="mb-3 form-label">
              {t('PROVIDE')} {t('CSV')}
            </label>
            <FileUploader
              fileTypes={CSV_FILE_TYPES}
              fileTypesLabel={t('CSV')}
              onChange={addFiles}
              ref={fileUploaderRef}
              multiple
            />
            <div className="separator mt-4 mb-4">
              <div className="separator-text form-label">
                {t('OR').toUpperCase()}
              </div>
            </div>
            <CargomaticInput
              type="textarea"
              className="mt-2"
              label={t('REFERENCE_NUMBERS_LABEL')}
              onChange={handleTextChange}
              placeholder={t('COMMA_OR_LINE_SEPARATED_LIST')}
              disabled={files.length > 0}
              value={text}
            />
            <div className="mt-2 flex justify-end">
              <button
                className="button button-hollow-green"
                disabled={!text || files.length > 0}
                onClick={handleParseReferenceNumbers}
              >
                {t('ADD')}
              </button>
            </div>
            {renderReferenceNumbers()}
          </div>
          {renderErrors()}

          <div className="p-4 flex justify-end mt-6 space-x-2 bg-gray-100">
            {files.length > 0 ? (
              <div className="">
                <div className="">{!uploadPending && renderAlerts()}</div>
                <div className="p-4 flex justify-end space-x-2 bg-gray-100">
                  <button
                    className="button button-secondary"
                    onClick={handleReset}
                  >
                    {t('RESET')}
                  </button>
                  <button
                    className="button button-green"
                    disabled={isResendDisabled()}
                    onClick={handleUpload}
                  >
                    {uploadPending && (
                      <LoadingCircle text={t('UPLOADING') + '...'} />
                    )}
                    {!uploadPending && (
                      <span>
                        {t('UPLOAD')} {t('CSV')}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="button button-green"
                disabled={isRunPrecheckDisabled()}
                onClick={handleRunPrecheck}
              >
                {runningPrecheck && (
                  <LoadingCircle text={t('CHECKING') + '...'} />
                )}
                {!runningPrecheck && <span>{t('CONTINUE')}</span>}
              </button>
            )}
          </div>
        </div>
      </div>
      {ranPrecheck && (
        <div className="">
          <div className="">{renderShipperRows()}</div>
          <div className="">{renderResults()}</div>
          <div className="p-4 flex justify-end space-x-2 bg-gray-100">
            <button className="button button-secondary" onClick={handleReset}>
              {t('RESET')}
            </button>
            <button
              className="button button-green"
              disabled={isResendDisabled()}
              onClick={handleResendEvents}
            >
              {resendingEvents && (
                <LoadingCircle text={t('SENDING_EVENTS') + '...'} />
              )}
              {!resendingEvents && <span>{t('RESEND')}</span>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
